import { AuthenticatedRealtimeService } from "../services/authenticated-realtime.service";
import { Component, OnDestroy, OnInit, Inject, DOCUMENT } from "@angular/core";
import { PlayerService } from "../services/player.service";
import { Player } from "../models/player";
import { AuthService } from "@auth0/auth0-angular";
import { Subscription } from "rxjs";

import { LocaleService } from '../i18n/locale.service';
import { AnalyticsService } from '../services/analytics.service';
import { ClientNotificationService } from '../services/client-notification.service';
import { ChatService } from '../services/chat.service';
import { AuthenticatedSessionStateService } from "../services/authenticated-session-state.service";

@Component({
    selector: "app-nav-bar",
    templateUrl: "./nav-bar.component.html",
    styleUrls: ["./nav-bar.component.css"],
    standalone: false
})
export class NavBarComponent implements OnInit, OnDestroy {
  public player: Player;
  public readonly notificationCount$ = this.clientNotifications.count$;
  public chatUnreadCount = 0;
  private stopRealtime: () => void;
  private chatChangedSubscription: Subscription;
  private unreadRevision = 0;
  private currentPlayerSubscription: Subscription;
  private chatRefreshTimer: any;
  private readonly chatRefreshHandler = () => this.refreshChatUnreadCount();

  constructor(
    public auth: AuthService,
    private playerService: PlayerService,
    private localeService: LocaleService,
    private analytics: AnalyticsService,
    private clientNotifications: ClientNotificationService,
    private chatService: ChatService,
    private realtime: AuthenticatedRealtimeService,
    private authenticatedSessionState: AuthenticatedSessionStateService,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  ngOnInit(): void {
    this.player = this.playerService.getCurrentPlayer();
    if (this.player) {
      this.clientNotifications.startAutoRefresh();
      this.startChatUnreadRefresh();
    }
    this.currentPlayerSubscription = this.playerService.currentPlayer$.subscribe((player) => {
      if (!player) {
        this.player = null;
        this.clientNotifications.stopAutoRefresh();
        this.stopChatUnreadRefresh();
        return;
      }

      this.player = player;
      this.clientNotifications.startAutoRefresh();
      this.startChatUnreadRefresh();
    });
    this.stopRealtime = this.realtime.subscribe(({type}) => { if (type !== 'heartbeat') this.refreshChatUnreadCount(); });
    this.chatChangedSubscription = this.chatService.changed$.subscribe(() => this.refreshChatUnreadCount());
    window.addEventListener("focus", this.chatRefreshHandler);
  }

  ngOnDestroy(): void {
    this.stopRealtime?.();
    this.chatChangedSubscription?.unsubscribe();
    this.unreadRevision++;
    if (this.currentPlayerSubscription) {
      this.currentPlayerSubscription.unsubscribe();
    }
    this.stopChatUnreadRefresh();
    window.removeEventListener("focus", this.chatRefreshHandler);
  }

  closeMobileMenu(): void {
    const mobileMenu = this.doc.getElementById("navbarNavMain");
    if (!mobileMenu || !mobileMenu.classList.contains("show")) {
      return;
    }

    mobileMenu.classList.remove("show");

    const mobileMenuToggle = this.doc.querySelector('[data-target="#navbarNavMain"]');
    if (mobileMenuToggle) {
      mobileMenuToggle.setAttribute("aria-expanded", "false");
      mobileMenuToggle.classList.add("collapsed");
    }
  }

  logout(): void {
    this.analytics.trackAndFlush("logout", { screen: "navigation" });
    this.localeService.clearUserCache(this.player && this.player.id);
    this.authenticatedSessionState.clear();
    this.playerService.clearCurrentPlayer();
    this.player = null;
    this.stopChatUnreadRefresh();
    this.auth.logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    } as any);
  }

  private startChatUnreadRefresh(): void {
    if (this.chatRefreshTimer) {
      return;
    }
    this.refreshChatUnreadCount();
    this.chatRefreshTimer = setInterval(() => this.refreshChatUnreadCount(), 60000);
  }

  private stopChatUnreadRefresh(): void {
    if (this.chatRefreshTimer) {
      clearInterval(this.chatRefreshTimer);
      this.chatRefreshTimer = null;
    }
    this.chatUnreadCount = 0;
  }

  private refreshChatUnreadCount(): void {
    const revision = ++this.unreadRevision;
    if (!this.player) {
      this.chatUnreadCount = 0;
      return;
    }
    this.chatService.getChatsForPlayer(this.player.id).subscribe(chats => {
      if (revision !== this.unreadRevision) return;
      this.chatUnreadCount = (chats || []).reduce((total, chat) => total + (chat.unreadCount || 0), 0);
    }, error => {
      console.log(error);
      this.chatUnreadCount = 0;
    });
  }
}
